import type { FC } from 'react';
import cn from 'classnames';
import { Button, FormHelperText, Modal } from '@selfinspectionorg/ui-kit';
import type { KeyedMutator } from 'swr';
import type { IMfaStatusView } from '@shared/services';
import styles from './mfa.module.scss';
import { useEmailSetup } from './useEmailSetup';
import { useCodeFactorDisable } from './useCodeFactorDisable';
import { StepUpModal } from './StepUpModal';
import PlusIcon from './assets/plus.svg?react';
import { SectionHeading } from '@/modules/profile/components';
import { EmailStepView } from '@/modules/auth/features/mfa/components';

interface Props {
  hasEmail: boolean;
  emailAvailable: boolean;
  /** Whether an authenticator factor is enabled; when true, email setup is not offered from here. */
  hasTotp: boolean;
  mutate: KeyedMutator<IMfaStatusView>;
}

/**
 * Email one-time-code section of the Profile Security tab. When the factor is not enabled it drives an
 * in-page setup wizard over the session-authorized management API: step-up password (which sends the
 * code) → enter the emailed code. When enabled it offers disabling the factor behind a confirmation
 * modal, like the authenticator section. `emailAvailable` is the code-by-email fallback surfaced as a
 * hint. The code factors are mutually exclusive, so setup is only offered when no authenticator factor
 * is enabled — with an authenticator on, this section stays status-only. Behaviour is unchanged.
 */
export const EmailSection: FC<Props> = ({ hasEmail, emailAvailable, hasTotp, mutate }) => {
  const setup = useEmailSetup(mutate);
  const disable = useCodeFactorDisable(mutate);

  // Only offer in-page email setup when the factor is off and no authenticator is enabled.
  const canSetUp = !hasEmail && !hasTotp;

  return (
    <section className={styles.section}>
      <SectionHeading title="Email" description="One-time codes sent to your email address." />
      <span
        className={cn(styles.status, hasEmail ? styles.statusEnabled : styles.statusDisabled)}
        data-testid="email-status"
      >
        {hasEmail ? 'Enabled' : 'Not enabled'}
      </span>

      {hasEmail ? (
        <>
          <p className={styles.hint}>Codes can be sent to your email when you sign in.</p>
          <div className={styles.actions}>
            <Button variant="danger" width="100%" onClick={disable.open} data-testid="disable-email-button">
              Disable
            </Button>
          </div>
        </>
      ) : (
        <>
          {emailAvailable && (
            <p className={styles.hint}>Email codes are also available as a fallback when you sign in.</p>
          )}
          {canSetUp && (
            <div className={styles.actions}>
              <Button onClick={setup.open} width="100%" data-testid="setup-email-button">
                <span className={styles.buttonContent}>
                  <PlusIcon />
                  Set up
                </span>
              </Button>
            </div>
          )}
        </>
      )}

      <StepUpModal
        isOpen={setup.step === 'stepup'}
        title="Confirm it's you"
        description="Enter your current password to set up email codes."
        password={setup.stepUp.password}
        onPasswordChange={setup.stepUp.setPassword}
        onSubmit={setup.submitStepUp}
        onClose={setup.close}
        isVerifying={setup.stepUp.isVerifying}
        error={setup.stepUp.error}
      />

      <Modal.Wrapper isOpen={setup.step === 'verify'} onClose={setup.close} id="emailSetupModal" width="400px">
        <Modal.Title
          titleText="Check your email"
          additionalText="We sent a 6-digit code to your email address. Enter it below to finish setup."
        />
        <EmailStepView
          code={setup.code}
          onCodeChange={setup.setCode}
          onSubmit={setup.submitCode}
          isSubmitting={setup.isVerifying}
          error={setup.verifyError}
        />
      </Modal.Wrapper>

      <Modal.Wrapper isOpen={disable.isConfirming} onClose={disable.close} id="disableEmailModal" width="400px">
        <Modal.Title
          titleText="Disable email codes?"
          additionalText="You'll no longer be asked for an emailed code when signing in. This cannot be undone."
        />
        {disable.error && (
          <FormHelperText errored data-testid="disable-email-error">
            {disable.error}
          </FormHelperText>
        )}
        <Modal.ActionButtons onCancelButtonClick={disable.close}>
          <Button
            variant="danger"
            width="132px"
            onClick={disable.disable}
            isLoading={disable.isDisabling}
            data-testid="confirm-disable-email-button"
          >
            Disable
          </Button>
        </Modal.ActionButtons>
      </Modal.Wrapper>
    </section>
  );
};
